/**
 * Flyout — the shell's Z3 overlay (docs/audits/PILOT-QUOTES-SHELL.md §4.2).
 *
 * Click / tap only (the floor device has no hover). An auto-hide overlay, 240 px wide,
 * over the content: it closes on a selection, Esc or an outside tap, and focus goes back
 * to the control that opened it. Pinning waits for S4's preferences, so it is not offered.
 *
 * It is a role="menu": the first entry takes focus on open; ↓ / ↑ move between the
 * menuitems (wrapping), Home / End jump, Enter selects, Tab leaves (closes, back to the
 * trigger — focus is never trapped). It is rendered into document.body, outside <main>,
 * so it follows the app theme rather than a page's legacy-light boundary.
 *
 *   placement 'side'   beside the trigger's right edge, from under the 64 px header down
 *                      (the module rail)
 *   placement 'below'  under the trigger, left-aligned with it (a header button)
 *
 * `header` (optional) renders above the menu, outside role="menu" — a control about the flyout
 * itself (S4b: Pin), not an entry in it.
 *
 * `viewportMargin` (optional, px; tablet pass 2026-10-20, item 2): once open, the flyout is moved
 * left as far as it takes to keep its right edge that far inside the viewport (and its left edge
 * too). The tool host's Tools menu passes 8: under a button near the right edge it ran past it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Flyout.css';

const HEADER_HEIGHT = 64;
const items = (panel) => (panel ? Array.from(panel.querySelectorAll('[role="menuitem"]')) : []);

/**
 * A menu's arrow keys: ↓ / ↑ move between its menuitems (wrapping), Home / End jump, Enter
 * selects. Returns true when it handled the key. Shared with the pinned column (Sidebar).
 */
export function menuKeyDown(e, container) {
  const list = items(container);
  const at = list.indexOf(document.activeElement);
  const move = (i) => {
    e.preventDefault();
    if (list.length) list[(i + list.length) % list.length].focus();
    return true;
  };
  switch (e.key) {
    case 'ArrowDown': return move(at + 1);
    case 'ArrowUp': return move(at < 0 ? -1 : at - 1);
    case 'Home': return move(0);
    case 'End': return move(-1);
    case 'Enter':
      if (e.target.getAttribute && e.target.getAttribute('role') === 'menuitem') {
        e.preventDefault();
        e.target.click();
        return true;
      }
      return false;
    default:
      return false;
  }
}

function positionFor(anchor, placement) {
  const rect = anchor ? anchor.getBoundingClientRect() : null;
  if (!rect) return { top: HEADER_HEIGHT, left: 0 };
  if (placement === 'below') return { top: rect.bottom + 4, left: Math.max(8, rect.left) };
  return { top: HEADER_HEIGHT, left: rect.right };
}

/** The left edge that keeps a box of `width` `margin` px inside a viewport `vw` wide. */
export function fitLeft(left, width, vw, margin) {
  return Math.max(margin, Math.min(left, vw - margin - width));
}

export default function Flyout({
  open, onClose, onSelect, label, triggerRef, placement = 'side', header = null, viewportMargin = null, children
}) {
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  const close = (returnFocus = true) => {
    onClose();
    if (returnFocus && triggerRef?.current) triggerRef.current.focus();
  };
  const closeRef = useRef(close);
  closeRef.current = close;

  useLayoutEffect(() => {
    if (open) setPos(positionFor(triggerRef?.current, placement));
  }, [open, placement, triggerRef]);

  // The clamp, measured once the flyout is laid out at its first position (it settles in one
  // pass: a fitted left fits again).
  useLayoutEffect(() => {
    if (!open || !pos || viewportMargin == null || !wrapRef.current) return;
    const width = wrapRef.current.getBoundingClientRect().width;
    const doc = document.documentElement;
    const vw = Math.min(window.innerWidth, (doc && doc.clientWidth) || window.innerWidth);
    const left = fitLeft(pos.left, width, vw, viewportMargin);
    if (left !== pos.left) setPos({ ...pos, left });
  }, [open, pos, viewportMargin]);

  // The first entry takes focus when the flyout opens.
  useEffect(() => {
    if (!open) return;
    const first = items(panelRef.current)[0];
    if (first) first.focus();
  }, [open]);

  // An outside tap closes it. A tap on the trigger is left to the trigger (it toggles).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const panel = wrapRef.current;
      const trigger = triggerRef?.current;
      if (panel && panel.contains(e.target)) return;
      if (trigger && trigger.contains(e.target)) return;
      closeRef.current(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, triggerRef]);

  if (!open) return null;

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      close();
    } else {
      menuKeyDown(e, panelRef.current);
    }
  };

  // A selection (any menuitem chosen) closes it; the link has already navigated.
  const onClick = (e) => {
    if (!(e.target.closest && e.target.closest('[role="menuitem"]'))) return;
    if (onSelect) onSelect();
    close();
  };

  return createPortal(
    <div
      ref={wrapRef}
      className={`shell-flyout shell-flyout-${placement}`}
      style={pos ? { top: `${pos.top}px`, left: `${pos.left}px` } : undefined}
    >
      {header}
      <div ref={panelRef} role="menu" aria-label={label} onKeyDown={onKeyDown} onClick={onClick}>
        {children}
      </div>
    </div>,
    document.body
  );
}
